import { useState, useCallback, useMemo, ReactNode } from "react";
import { usePanelRef } from "react-resizable-panels";
import { useIsLastPressKey } from "@hooks/useIsLastPressKey";
import { isBusyClosesDemoId } from "@components/demo/busyClosesDemo/busyClosesDemoIds";
import type {
    TaskDetailPanelType,
    TaskDetailSectionRequest,
} from "./TaskDetail/constants";
import {
    OpenTaskParams,
    TaskPaneState,
    TaskPaneStateContext,
    TasksContextValue,
    type TaskDetailSectionTarget,
} from "./tasks.context";
import { TasksContextProvider } from "./TasksContextProvider";
import { TaskComposerAutoFocusContext } from "./taskComposerAutoFocus.context";
import { useOpenTaskUrl } from "./useOpenTaskUrl";

interface TasksProviderProps {
    children: ReactNode;
}

/** The task paging went to; it stays marked until the next navigation after it arrives. */
interface PagedTask {
    taskId: string;
    hasArrived: boolean;
}

/** The pane tab each one-off section request shows. */
const SECTION_REQUEST_TABS = {
    dependencies: "detail",
    details: "detail",
} as const satisfies Record<TaskDetailSectionRequest, TaskDetailPanelType>;

export const TasksProvider = ({ children }: TasksProviderProps) => {
    const {
        selectedTaskId,
        openTask: openTaskUrl,
        goToTask: goToTaskUrl,
        closeTask,
    } = useOpenTaskUrl();
    const isLastPressKey = useIsLastPressKey();
    const [isOpenedByKeyboard, setIsOpenedByKeyboard] = useState(false);
    const [pagedTask, setPagedTask] = useState<PagedTask | null>(null);
    // The router applies the URL in a transition, after `goToTask` marks the task.
    if (
        pagedTask?.hasArrived === false &&
        selectedTaskId === pagedTask.taskId
    ) {
        setPagedTask({ ...pagedTask, hasArrived: true });
    } else if (pagedTask?.hasArrived && selectedTaskId !== pagedTask.taskId) {
        setPagedTask(null);
    }
    const taskDetailPanelRef = usePanelRef();
    const [taskDetailPanelCollapsed, setTaskDetailPanelCollapsed] =
        useState(false);
    const [paneContent, setPaneContent] = useState<TaskDetailPanelType | null>(
        null,
    );

    const openTask = useCallback(
        (params: OpenTaskParams) => {
            if (isBusyClosesDemoId(params.taskId)) {
                return;
            }
            setIsOpenedByKeyboard(isLastPressKey());
            setPaneContent(null);
            setPagedTask(null);
            openTaskUrl(params);
        },
        [isLastPressKey, openTaskUrl],
    );
    const goToTask = useCallback(
        (params: OpenTaskParams) => {
            if (isBusyClosesDemoId(params.taskId)) {
                return;
            }
            setPagedTask({ taskId: params.taskId, hasArrived: false });
            goToTaskUrl(params);
        },
        [goToTaskUrl],
    );
    const requestTaskDetailSection = useCallback(
        ({ section }: TaskDetailSectionTarget) =>
            setPaneContent(SECTION_REQUEST_TABS[section]),
        [],
    );

    const showTaskDetailPanel = useCallback(
        (type: TaskDetailPanelType) => {
            setPaneContent(type);
            taskDetailPanelRef.current?.expand();
        },
        [taskDetailPanelRef],
    );

    const value: TasksContextValue = useMemo(
        () => ({
            selectedTaskId,
            isOpenedByKeyboard,
            openTask,
            goToTask,
            closeTask,
            taskDetailPanelRef,
            setTaskDetailPanelCollapsed,
            setTaskDetailPanelContent: setPaneContent,
            requestTaskDetailSection,
            showTaskDetailPanel,
        }),
        [
            selectedTaskId,
            isOpenedByKeyboard,
            openTask,
            goToTask,
            closeTask,
            taskDetailPanelRef,
            requestTaskDetailSection,
            showTaskDetailPanel,
        ],
    );

    const paneState: TaskPaneState = useMemo(
        () => ({
            taskDetailPanelCollapsed,
            taskDetailPanelContent: paneContent,
        }),
        [taskDetailPanelCollapsed, paneContent],
    );

    return (
        <TasksContextProvider value={value}>
            <TaskPaneStateContext.Provider value={paneState}>
                <TaskComposerAutoFocusContext.Provider
                    value={pagedTask === null}
                >
                    {children}
                </TaskComposerAutoFocusContext.Provider>
            </TaskPaneStateContext.Provider>
        </TasksContextProvider>
    );
};
